import { Link2, X } from 'lucide-react';
import { useState, type ReactElement } from 'react';
import { Link } from 'react-router';

import { ConfirmModal } from '@/components/ui/ConfirmModal';
import { useToast } from '@/components/ui/toast';
import { StatusBadge } from '@/features/master-list/components/StatusBadge';
import { usePermission } from '@/hooks/usePermission';
import { useBackState } from '@/lib/back-nav';
import type { DocumentStatus } from '@/lib/document-status';

import { relationError, relationLabel } from '../presentation';
import type { ImpactRow } from '../types';
import { useDocumentImpact, useUnrelate } from '../use-relations';
import { AddRelationModal } from './AddRelationModal';
import { skeletonLine } from '@/components/ui/skeleton';

// Documentos relacionados de la ficha (spec §6.2.6): los que este documento menciona
// o impacta y los que lo mencionan a él. Calidad agrega y quita relaciones; quitar
// pide confirmación. Un documento anulado no admite relaciones nuevas.

export function RelatedDocumentsCard({
  entryId,
  code,
  status,
}: {
  entryId: string;
  code: string;
  status: DocumentStatus;
}): ReactElement {
  const canRelate = usePermission('quality:document:relate');
  const impact = useDocumentImpact(entryId, true);
  const unrelate = useUnrelate(entryId);
  const { showToast } = useToast();
  const [adding, setAdding] = useState(false);
  const [removing, setRemoving] = useState<ImpactRow | null>(null);
  const fromHere = useBackState(`Ficha ${code}`);
  const rows = impact.data ?? [];
  const canAdd = canRelate && status !== 'anulado';

  async function confirmRemove(): Promise<void> {
    if (removing === null) {
      return;
    }
    try {
      await unrelate.mutateAsync(removing.entryId);
      showToast(`Relación con ${removing.code} quitada`);
      setRemoving(null);
    } catch (err) {
      showToast(relationError(err), 'error');
    }
  }

  return (
    <div className="card rise d3">
      <div className="ch">
        <h3>Documentos relacionados</h3>
        {canAdd && (
          <button type="button" className="btn mut sm" onClick={() => setAdding(true)}>
            <Link2 size={14} aria-hidden="true" />
            Relacionar documento
          </button>
        )}
      </div>
      <div className="cb">
        {impact.isError ? (
          <div className="fld-error" role="alert">
            No se pudieron cargar los documentos relacionados.
            <button type="button" className="btn mut sm" onClick={() => void impact.refetch()}>
              Reintentar
            </button>
          </div>
        ) : impact.isLoading ? (
          <div
            aria-busy="true"
            aria-label="Cargando documentos relacionados"
            style={{ display: 'grid', gap: 8 }}
          >
            <div className={skeletonLine({ width: 80 })} />
            <div className={skeletonLine({ width: 60 })} />
          </div>
        ) : rows.length === 0 ? (
          <p className="soft" style={{ fontSize: 13, margin: 0 }}>
            Sin documentos relacionados.
          </p>
        ) : (
          <ul className="rel-list">
            {rows.map((r) => (
              <li key={`${r.direction}-${r.entryId}`} className="rel-item">
                <div className="rel-main">
                  <Link to={`/documentos/${r.entryId}`} state={fromHere} className="code">
                    {r.code}
                  </Link>{' '}
                  <span>{r.title ?? ''}</span>
                  <div className="soft" style={{ fontSize: 12 }}>
                    {relationLabel(r)}
                  </div>
                </div>
                <StatusBadge status={r.status} nextReviewAt={null} />
                {canRelate && (
                  <button
                    type="button"
                    className="icon-btn"
                    aria-label={`Quitar relación con ${r.code}`}
                    onClick={() => setRemoving(r)}
                  >
                    <X size={14} aria-hidden="true" />
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
      {adding && (
        <AddRelationModal
          entryId={entryId}
          code={code}
          excluded={rows.map((r) => r.entryId)}
          onClose={() => setAdding(false)}
        />
      )}
      <ConfirmModal
        open={removing !== null}
        title="Quitar relación"
        message={`${code} y ${removing?.code ?? ''} dejarán de estar relacionados. La relación se puede volver a agregar.`}
        confirmLabel="Quitar"
        destructive
        busy={unrelate.isPending}
        onConfirm={() => void confirmRemove()}
        onCancel={() => setRemoving(null)}
      />
    </div>
  );
}
